// src/components/shared/data-display/LoadingSpinner.tsx

'use client';

import Logo from '@/components/shared/misc/Logo';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { cn } from '@/lib/utils/cn';
import { cssVars } from '@/styles/theme';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  className?: string;
  fullScreen?: boolean;
  text?: string;
}

const sizeStyles: Record<SpinnerSize, { width: number; height: number; ringSize: number }> = {
  sm: { width: 64, height: 64, ringSize: 100 },
  md: { width: 80, height: 80, ringSize: 120 },
  lg: { width: 120, height: 120, ringSize: 180 },
  xl: { width: 160, height: 160, ringSize: 240 },
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className,
  fullScreen = false,
  text,
}) => {
  const [mounted, setMounted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');

  useEffect(() => {
    setMounted(true);
  }, []);

  // على الجوال استخدم مقاساً أصغر عند fullScreen ليتناسب مع الشاشة
  const effectiveSize = fullScreen && isMobile ? 'sm' : size;
  const dimensions = sizeStyles[effectiveSize];

  const spinner = (
    <div className={cn('relative flex flex-col items-center justify-center gap-4 sm:gap-6', className)}>
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0 -z-10 rounded-full blur-3xl opacity-20"
        style={{
          background: `radial-gradient(circle, ${cssVars.primary.DEFAULT} 0%, ${cssVars.accent.primary} 50%, transparent 70%)`,
          width: dimensions.ringSize * 1.5,
          height: dimensions.ringSize * 1.5,
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Outer rotating ring */}
      <div className="relative flex items-center justify-center">
        <motion.div
          className="absolute rounded-full"
          style={{
            width: dimensions.ringSize,
            height: dimensions.ringSize,
            border: `3px solid transparent`,
            borderTopColor: cssVars.primary.DEFAULT,
            borderRightColor: cssVars.accent.primary,
            borderBottomColor: cssVars.accent.warm,
            borderLeftColor: cssVars.primary.light,
            boxShadow: `0 0 20px ${cssVars.primary.DEFAULT}40, inset 0 0 20px ${cssVars.accent.primary}20`,
          }}
          animate={mounted ? { rotate: 360 } : {}}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Middle pulsing ring */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: dimensions.ringSize * 0.85,
            height: dimensions.ringSize * 0.85,
            border: `2px solid ${cssVars.accent.primary}`,
            opacity: 0.6,
            boxShadow: `0 0 15px ${cssVars.accent.primary}50`,
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Fixed logo container (no rotation) */}
        <div className="relative z-10 flex items-center justify-center">
          {/* Glow effect behind logo */}
          <motion.div
            className="absolute rounded-full blur-xl"
            style={{
              width: dimensions.width * 1.2,
              height: dimensions.height * 1.2,
              background: `radial-gradient(circle, ${cssVars.primary.DEFAULT}60, transparent 70%)`,
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Fixed Logo (no rotation, no pulsing) */}
          <Logo
            width={dimensions.width}
            height={dimensions.height}
            withName={true}
            showText={false}
            className="relative z-10 block drop-shadow-lg"
          />
        </div>
      </div>

      {/* Loading dots below logo */}
      <div className="flex items-center gap-2">
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className="rounded-full"
            style={{
              width: effectiveSize === 'sm' ? '8px' : effectiveSize === 'md' ? '10px' : effectiveSize === 'lg' ? '12px' : '14px',
              height: effectiveSize === 'sm' ? '8px' : effectiveSize === 'md' ? '10px' : effectiveSize === 'lg' ? '12px' : '14px',
              backgroundColor: cssVars.primary.DEFAULT,
            }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.4, 1, 0.4],
            }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.2,
            }}
          />
        ))}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center p-4 sm:p-6"
        style={{
          background: `radial-gradient(ellipse at center, ${cssVars.neutral.bg} 0%, ${cssVars.neutral.surface} 100%)`,
        }}
      >
        <div className="w-full max-w-[min(90vw,320px)] flex flex-col items-center justify-center">
          {spinner}
        </div>
      </div>
    );
  }

  return spinner;
};

export default LoadingSpinner;
